/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseSettlementP2Page.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/settlements/p2 二回目精算（spec §8.2 / 工程規格書 §6.4
 *                ⑤sheet T33/T37/T38）— 逐品二回目重量 + 三方分配 → 試算 → T38 手入力。
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端：v13_settlement_runs calculate_second / second_manual_amount。
 *  - POST /cases/:id/settlements/p2/calculate → 每次新 run（non-overwrite）。
 *  - POST /settlements/p2/runs/:run_id/manual-amount → T38 EF 實際請求金額手入力。
 *  使用者面 label 不露 cell 代碼：T33 → 二回目試算総額；T37 → 二回目請求概算。
 *  三方分配：每品可選填 P/Q/R（不填則後端套預設 20/40/40）；填則三者皆須填且
 *  P+Q+R=K（前端先驗，後端嚴格校驗，不過 → 409/422 + 進 AuditRequired）。
 *  RBAC：calc / manual = marketplace.settlements.manual_amount.edit
 *        （PERMISSIONS.settlementP2）。
 *  ⚠️ 金額・重量・単価一律字串型 Decimal，前端不得轉 float 做金額累積（CLAUDE.md §2）。
 */

import { useMemo, useState, type ReactElement } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCase, type CaseItemDto } from '../api/cases'
import { listAwards, type AwardDto } from '../api/auction'
import { listMemberDecisions, type MemberDecisionDto } from '../api/adjustments'
import {
  calculateP2,
  confirmP2Payment,
  getLatestP2,
  issueP2,
  submitP2ManualAmount,
  type P2ResultDto,
} from '../api/settlements'
import { getWorkflowState } from '../api/workflow'
import { ApiError } from '../api/client'
import { validateThreeWaySplit } from '../lib/settlementShare'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { useStepUp } from '../components/StepUpDialog'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

function fmtAmount(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  return Number.isFinite(num) ? num.toLocaleString('ja-JP', { maximumFractionDigits: 2 }) : value
}

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

/** 可二回目精算品項（confirmed 落札 且 非 return/hold 決策）。 */
interface CalcableItem {
  item: CaseItemDto
  /** 落札単価 J。 */
  j: string
  /** メンバー調整後単価 M（price_adjust → adjusted；否則 min_unit_price）。 */
  m: string
}

/** 帶 T38 的 P2 run 結果。 */
interface RunEntry {
  result: P2ResultDto
  /** T38 EF 實際請求金額（手入力，未填為 null）。 */
  manualAmount: string | null
}

export function CaseSettlementP2Page({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canCalculate = can(PERMISSIONS.settlementP2)

  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })
  const awardsQuery = useQuery({
    queryKey: ['case', caseId, 'awards'],
    queryFn: () => listAwards(caseId),
    enabled: caseId !== '',
  })
  const decisionsQuery = useQuery({
    queryKey: ['case', caseId, 'member-decisions'],
    queryFn: () => listMemberDecisions(caseId),
    enabled: caseId !== '',
  })
  const latestRunQuery = useQuery({
    queryKey: ['case', caseId, 'p2-latest'],
    queryFn: () => getLatestP2(caseId),
    enabled: caseId !== '',
  })
  const workflowQuery = useQuery({
    queryKey: ['case', caseId, 'workflow-state'],
    queryFn: () => getWorkflowState(caseId),
    enabled: caseId !== '',
  })

  const [runs, setRuns] = useState<RunEntry[]>([])
  const [manualInput, setManualInput] = useState('')

  const items = useMemo<CaseItemDto[]>(() => caseQuery.data?.items ?? [], [caseQuery.data])
  const awards = useMemo<AwardDto[]>(() => awardsQuery.data ?? [], [awardsQuery.data])
  const decisions = useMemo<MemberDecisionDto[]>(
    () => decisionsQuery.data ?? [],
    [decisionsQuery.data],
  )

  const calcable = useMemo<CalcableItem[]>(() => {
    const awardByItem = new Map<number, string>()
    for (const a of awards) awardByItem.set(a.item_id, a.auction_unit_price)
    const decidedByItem = new Map<number, MemberDecisionDto>()
    for (const d of decisions) if (d.status === 'decided') decidedByItem.set(d.item_id, d)

    const out: CalcableItem[] = []
    for (const item of items) {
      const j = awardByItem.get(item.id)
      if (j === undefined) continue
      const decision = decidedByItem.get(item.id)
      // return / hold 從二回目精算排除（後端 calculate_second 同義拒絕）。
      if (decision && (decision.decision_type === 'return' || decision.decision_type === 'hold')) {
        continue
      }
      const mStr =
        decision?.decision_type === 'price_adjust' && decision.adjusted_member_unit_price
          ? decision.adjusted_member_unit_price
          : item.min_unit_price
      if (mStr === null || mStr === undefined) continue
      out.push({ item, j, m: mStr })
    }
    return out
  }, [items, awards, decisions])

  const isLoading =
    caseQuery.isPending ||
    awardsQuery.isPending ||
    decisionsQuery.isPending ||
    latestRunQuery.isPending

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }
  if (caseQuery.isError || !caseQuery.data) {
    return (
      <p className="text-sm text-destructive">
        {t('case.detail.loadError')}
        {errDetail(caseQuery.error)}
      </p>
    )
  }

  const caseNo = caseQuery.data.case_no
  const latestServerRun = latestRunQuery.data ?? null
  const latestLocalRun = runs[0]

  // 発行/入金確認 用的 run_id：優先本 session 已設 T38 的 run，否則 server 最新 run。
  const issuableRunId =
    runs.find((r) => r.manualAmount !== null)?.result.run_id ?? latestServerRun?.run_id ?? null
  const serverManualSet =
    latestServerRun?.manual_amount !== null && latestServerRun?.manual_amount !== undefined
  const localManualSet = runs.some((r) => r.manualAmount !== null)
  const t38Set = localManualSet || serverManualSet
  const workflowState = workflowQuery.data?.workflow_state ?? null
  const allowedNext = workflowQuery.data?.allowed_next ?? []

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.p2')}</span>
        <h2 className="text-lg font-semibold">{t('p2.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('p2.subtitle')}</p>
      </header>

      {/* 既存 server-side 最新 run（頁面首載時提供脈絡）*/}
      <Card>
        <CardHeader>
          <CardTitle>{t('p2.latest.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {latestServerRun ? (
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <ResultStat
                label={t('p2.result.total')}
                value={`¥${fmtAmount(latestServerRun.t33_excl_tax)}`}
                emphasis
              />
              <ResultStat
                label={t('p2.result.estimate')}
                value={`¥${fmtAmount(latestServerRun.t37_estimate)}`}
              />
              <ResultStat
                label={t('p2.manual.label')}
                value={`¥${fmtAmount(latestServerRun.manual_amount)}`}
              />
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">{t('p2.latest.none')}</p>
          )}
        </CardContent>
      </Card>

      {/* 発行 → 入金確認（lifecycle 推進；run 存在 + T38 已設後可発行）*/}
      <IssuePaymentCard
        t={t}
        caseId={caseId}
        runId={issuableRunId}
        t38Set={t38Set}
        workflowState={workflowState}
        allowedNext={allowedNext}
        canAct={canCalculate}
        onAdvanced={() => {
          void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'workflow-state'] })
          void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'p2-latest'] })
        }}
      />

      {/* 試算品項格（react-hook-form 動態列）*/}
      <Card>
        <CardHeader>
          <CardTitle>{t('p2.items.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">{t('p2.shareHint')}</p>
          {!canCalculate ? (
            <Badge variant="outline">{t('rbac.forbidden')}</Badge>
          ) : calcable.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('p2.items.empty')}</p>
          ) : (
            <P2Form
              t={t}
              caseNo={caseNo}
              calcable={calcable}
              onCalculate={(body) => calculateP2(caseId, body)}
              onSuccess={(result) => {
                setRuns((prev) => [{ result, manualAmount: null }, ...prev])
                void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'p2-latest'] })
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* 最新試算結果（本 session）— 使用者面 label */}
      {latestLocalRun ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('p2.result.title')}（run #{latestLocalRun.result.run_id}）
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ResultStat
                label={t('p2.result.total')}
                value={`¥${fmtAmount(latestLocalRun.result.t33_excl_tax)}`}
                emphasis
              />
              <ResultStat
                label={t('p2.result.estimate')}
                value={`¥${fmtAmount(latestLocalRun.result.t37_estimate)}`}
              />
            </dl>

            {/* 逐品明細 */}
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('p2.result.itemsTitle')}</TableHead>
                    <TableHead className="text-right">{t('p2.field.k')}</TableHead>
                    <TableHead className="text-right">{t('p2.field.p')}</TableHead>
                    <TableHead className="text-right">{t('p2.field.q')}</TableHead>
                    <TableHead className="text-right">{t('p2.field.r')}</TableHead>
                    <TableHead className="text-right">{t('p2.result.sUnit')}</TableHead>
                    <TableHead className="text-right">{t('p2.field.weight')}</TableHead>
                    <TableHead className="text-right">{t('p2.result.tSubtotal')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {latestLocalRun.result.items.map((it) => (
                    <TableRow key={it.case_item_id}>
                      <TableCell className="font-medium">{it.item_management_no}</TableCell>
                      <TableCell className="text-right">¥{fmtAmount(it.k_diff)}</TableCell>
                      <TableCell className="text-right">¥{fmtAmount(it.p_ef_share)}</TableCell>
                      <TableCell className="text-right">¥{fmtAmount(it.q_member_share)}</TableCell>
                      <TableCell className="text-right">¥{fmtAmount(it.r_venue_share)}</TableCell>
                      <TableCell className="text-right">¥{fmtAmount(it.s_unit)}</TableCell>
                      <TableCell className="text-right">
                        {fmtAmount(it.second_settlement_weight_kg)}
                      </TableCell>
                      <TableCell className="text-right">¥{fmtAmount(it.t_subtotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* T38 EF 實際請求金額（手入力）*/}
            <ManualAmountPanel
              t={t}
              runId={latestLocalRun.result.run_id}
              saved={latestLocalRun.manualAmount}
              canEdit={canCalculate}
              value={manualInput}
              onChange={setManualInput}
              onSaved={(amount) => {
                setRuns((prev) =>
                  prev.map((entry) =>
                    entry.result.run_id === latestLocalRun.result.run_id
                      ? { ...entry, manualAmount: amount }
                      : entry,
                  ),
                )
                setManualInput('')
                void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'p2-latest'] })
              }}
            />
          </CardContent>
        </Card>
      ) : null}

      {/* run 歷史 — 證明 re-calc 產生新 run，不覆蓋 */}
      {runs.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('p2.history.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('p2.history.run')}</TableHead>
                    <TableHead className="text-right">{t('p2.result.total')}</TableHead>
                    <TableHead className="text-right">{t('p2.result.estimate')}</TableHead>
                    <TableHead className="text-right">{t('p2.manual.label')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((entry) => (
                    <TableRow key={entry.result.run_id}>
                      <TableCell>#{entry.result.run_id}</TableCell>
                      <TableCell className="text-right">
                        ¥{fmtAmount(entry.result.t33_excl_tax)}
                      </TableCell>
                      <TableCell className="text-right">
                        ¥{fmtAmount(entry.result.t37_estimate)}
                      </TableCell>
                      <TableCell className="text-right">
                        {entry.manualAmount !== null ? `¥${fmtAmount(entry.manualAmount)}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}

// ---- 試算表單（react-hook-form + zod）-------------------------------------

function P2Form({
  t,
  caseNo,
  calcable,
  onCalculate,
  onSuccess,
}: {
  t: (key: string) => string
  caseNo: string
  calcable: CalcableItem[]
  onCalculate: (body: {
    case_no: string
    items: {
      case_item_id: number
      second_settlement_weight_kg: string
      p_ef_share?: string | null
      q_member_share?: string | null
      r_venue_share?: string | null
    }[]
  }) => Promise<P2ResultDto>
  onSuccess: (result: P2ResultDto) => void
}): ReactElement {
  // 每列：重量必填 + > 0；P/Q/R 全空或全填，若全填則 P+Q+R = K（K=J−M）。
  const itemSchema = z
    .object({
      caseItemId: z.number(),
      j: z.string(),
      m: z.string(),
      weight: z
        .string()
        .trim()
        .min(1)
        .refine((v) => Number.isFinite(Number(v)) && Number(v) > 0, { message: 'positive' }),
      p: z.string().trim().optional().default(''),
      q: z.string().trim().optional().default(''),
      r: z.string().trim().optional().default(''),
    })
    .superRefine((val, ctx) => {
      const filled = [val.p, val.q, val.r].filter((x) => x.trim() !== '')
      if (filled.length === 0) return // 全空 → 後端套預設 20/40/40
      if (filled.length !== 3) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['p'], message: 'pqr_all_or_none' })
        return
      }
      const p = Number(val.p)
      const q = Number(val.q)
      const r = Number(val.r)
      if (![p, q, r].every(Number.isFinite)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['p'], message: 'pqr_numeric' })
        return
      }
      const k = Number(val.j) - Number(val.m)
      if (!validateThreeWaySplit(p, q, r, k)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['p'], message: 'pqr_sum' })
      }
    })

  const formSchema = z.object({ items: z.array(itemSchema) })
  type FormValues = z.input<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      items: calcable.map((c) => ({
        caseItemId: c.item.id,
        j: c.j,
        m: c.m,
        weight: c.item.estimated_weight_kg ?? '',
        p: '',
        q: '',
        r: '',
      })),
    },
  })

  const { fields } = useFieldArray({ control: form.control, name: 'items' })

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      onCalculate({
        case_no: caseNo,
        items: values.items.map((row) => {
          const hasShares = [row.p, row.q, row.r].every((x) => (x ?? '').trim() !== '')
          return {
            case_item_id: row.caseItemId,
            second_settlement_weight_kg: row.weight.trim(),
            p_ef_share: hasShares ? row.p!.trim() : null,
            q_member_share: hasShares ? row.q!.trim() : null,
            r_venue_share: hasShares ? row.r!.trim() : null,
          }
        }),
      }),
    onSuccess: (result) => {
      toast.success(t('p2.calc.success'))
      onSuccess(result)
    },
    onError: (error) => toast.error(`${t('p2.calc.error')}${errDetail(error)}`),
  })

  const submit = form.handleSubmit((values) => mutation.mutate(values))

  return (
    <form className="space-y-3" onSubmit={(e) => void submit(e)}>
      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('shares.item')}</TableHead>
              <TableHead className="text-right">{t('p2.field.j')}</TableHead>
              <TableHead className="text-right">{t('p2.field.m')}</TableHead>
              <TableHead className="text-right">{t('p2.field.k')}</TableHead>
              <TableHead className="min-w-[8rem]">{t('p2.field.weight')}</TableHead>
              <TableHead className="min-w-[6rem]">{t('p2.field.p')}</TableHead>
              <TableHead className="min-w-[6rem]">{t('p2.field.q')}</TableHead>
              <TableHead className="min-w-[6rem]">{t('p2.field.r')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => {
              const row = calcable[index]
              if (!row) return null
              const k = Number(row.j) - Number(row.m)
              const rowErr = form.formState.errors.items?.[index]
              return (
                <TableRow key={field.id}>
                  <TableCell className="font-medium">
                    #{row.item.line_no} {row.item.item_name}
                  </TableCell>
                  <TableCell className="text-right">¥{fmtAmount(row.j)}</TableCell>
                  <TableCell className="text-right">¥{fmtAmount(row.m)}</TableCell>
                  <TableCell className="text-right font-semibold">
                    ¥{Number.isFinite(k) ? k.toLocaleString('ja-JP') : '—'}
                  </TableCell>
                  <TableCell>
                    <Input
                      inputMode="decimal"
                      aria-invalid={!!rowErr?.weight}
                      aria-label={t('p2.field.weight')}
                      {...form.register(`items.${index}.weight`)}
                    />
                  </TableCell>
                  <TableCell>
                    <Controller
                      control={form.control}
                      name={`items.${index}.p`}
                      render={({ field: ctrl }) => (
                        <Input
                          inputMode="decimal"
                          aria-invalid={!!rowErr?.p}
                          aria-label={t('p2.field.p')}
                          value={ctrl.value ?? ''}
                          onChange={ctrl.onChange}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Controller
                      control={form.control}
                      name={`items.${index}.q`}
                      render={({ field: ctrl }) => (
                        <Input
                          inputMode="decimal"
                          aria-label={t('p2.field.q')}
                          value={ctrl.value ?? ''}
                          onChange={ctrl.onChange}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Controller
                      control={form.control}
                      name={`items.${index}.r`}
                      render={({ field: ctrl }) => (
                        <Input
                          inputMode="decimal"
                          aria-label={t('p2.field.r')}
                          value={ctrl.value ?? ''}
                          onChange={ctrl.onChange}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      <Button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? t('p2.calc.running') : t('p2.calc.button')}
      </Button>
    </form>
  )
}

// ---- T38 手入力面板 -------------------------------------------------------

function ManualAmountPanel({
  t,
  runId,
  saved,
  canEdit,
  value,
  onChange,
  onSaved,
}: {
  t: (key: string) => string
  runId: number
  saved: string | null
  canEdit: boolean
  value: string
  onChange: (value: string) => void
  onSaved: (amount: string) => void
}): ReactElement {
  // T38 手入力 = 高風險端點 → 送出前先 2FA step-up（spec §9.2）。
  const stepUp = useStepUp(t)
  const mutation = useMutation({
    mutationFn: (vars: { amount: string; code: string }) =>
      submitP2ManualAmount(runId, { amount: vars.amount }, vars.code),
    onSuccess: (_result, vars) => {
      toast.success(t('p2.manual.success'))
      onSaved(vars.amount)
    },
    onError: (error) => toast.error(`${t('p2.manual.error')}${errDetail(error)}`),
  })

  return (
    <div className="rounded-md border border-border p-4">
      <p className="text-sm font-medium">{t('p2.manual.label')}</p>
      <p className="mb-2 text-xs text-muted-foreground">{t('p2.manual.hint')}</p>
      {saved !== null ? (
        <p className="text-lg font-semibold">
          ¥{fmtAmount(saved)} <Badge variant="secondary">{t('p2.manual.saved')}</Badge>
        </p>
      ) : canEdit ? (
        <form
          className="flex max-w-md gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            const amount = value.trim()
            if (amount) stepUp.request((code) => mutation.mutate({ amount, code }))
          }}
        >
          <Input
            inputMode="decimal"
            value={value}
            placeholder={t('p2.manual.placeholder')}
            aria-label={t('p2.manual.label')}
            data-testid="p2-manual-amount-input"
            onChange={(e) => onChange(e.target.value)}
          />
          <Button
            type="submit"
            data-testid="p2-manual-amount-submit"
            disabled={mutation.isPending || !value.trim()}
          >
            {mutation.isPending ? t('p2.manual.saving') : t('p2.manual.submit')}
          </Button>
          {stepUp.dialog}
        </form>
      ) : (
        <Badge variant="outline">{t('rbac.forbidden')}</Badge>
      )}
    </div>
  )
}

// ---- 発行 / 入金確認 カード -----------------------------------------------

/** P2 発行目標狀態 / 入金確認目標狀態（workflow allowed_next 判定可達性）。 */
const P2_ISSUED_NEXT = 'P2Issued'
const P2_PAYMENT_NEXT = 'P2PaymentConfirmed'

function IssuePaymentCard({
  t,
  caseId,
  runId,
  t38Set,
  workflowState,
  allowedNext,
  canAct,
  onAdvanced,
}: {
  t: (key: string) => string
  caseId: string
  runId: number | null
  t38Set: boolean
  workflowState: string | null
  allowedNext: string[]
  canAct: boolean
  onAdvanced: () => void
}): ReactElement {
  const canAdvanceIssue = allowedNext.includes(P2_ISSUED_NEXT)
  const canAdvancePayment = allowedNext.includes(P2_PAYMENT_NEXT)

  const issueMutation = useMutation({
    mutationFn: () => issueP2(caseId, runId as number),
    onSuccess: () => {
      toast.success(t('p2.issue.success'))
      onAdvanced()
    },
    onError: (error) => toast.error(`${t('p2.issue.error')}${errDetail(error)}`),
  })
  const paymentMutation = useMutation({
    mutationFn: () => confirmP2Payment(caseId),
    onSuccess: () => {
      toast.success(t('p2.payment.success'))
      onAdvanced()
    },
    onError: (error) => toast.error(`${t('p2.payment.error')}${errDetail(error)}`),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('p2.issue.cardTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('p2.issue.currentState')}</span>
          <Badge variant="secondary">{workflowState ?? '—'}</Badge>
        </div>

        {!canAct ? (
          <Badge variant="outline">{t('rbac.forbidden')}</Badge>
        ) : (
          <>
            {/* 発行 */}
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('p2.issue.label')}</p>
              {canAdvanceIssue ? (
                <>
                  <Button
                    type="button"
                    data-testid="p2-issue"
                    disabled={issueMutation.isPending || runId === null || !t38Set}
                    onClick={() => issueMutation.mutate()}
                  >
                    {issueMutation.isPending ? t('p2.issue.running') : t('p2.issue.button')}
                  </Button>
                  {runId === null || !t38Set ? (
                    <p className="text-xs text-destructive">{t('p2.issue.needT38')}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t('p2.issue.hint')}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">{t('p2.issue.notReady')}</p>
              )}
            </div>

            {/* 入金確認 */}
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('p2.payment.label')}</p>
              {canAdvancePayment ? (
                <Button
                  type="button"
                  variant="outline"
                  data-testid="p2-payment-confirm"
                  disabled={paymentMutation.isPending}
                  onClick={() => paymentMutation.mutate()}
                >
                  {paymentMutation.isPending ? t('p2.payment.running') : t('p2.payment.button')}
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">{t('p2.payment.notReady')}</p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function ResultStat({
  label,
  value,
  emphasis,
}: {
  label: string
  value: string
  emphasis?: boolean
}): ReactElement {
  return (
    <div className="rounded-md border border-border p-4">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={emphasis ? 'text-2xl font-bold' : 'text-xl font-semibold'}>{value}</dd>
    </div>
  )
}
